import React, { useEffect, useState } from 'react';
import { counter } from '../../../../tools/Counter';
import Table from '../../../../components/base/Table';
import { ethFormat } from '../../../../utils/singleAssetPage/parseData';
import { getEthPrice } from '../../../../utils/values';
import styles from './List.module.scss';
import Gmail from '../../../../assets/icons/domains/gmail.svg';
import GoogleAds from '../../../../assets/icons/domains/google_ads.svg';
import LinkedIn from '../../../../assets/icons/domains/linkedin.svg';
import Search from '../../../../assets/icons/domains/search.svg';
import Twitter from '../../../../assets/icons/domains/twitter.svg';
import Youtube from '../../../../assets/icons/domains/youtube.svg';
import Export from '../../../../components/ui/Buttons/Export';
import { webSDKApi } from '../../../../api/webSDK';
import DownloadModal from '../../../../components/ui/modals/DownloadModal';
import { useParams } from 'react-router-dom';
import { getMaxValueByProperty, omitKey } from '../../../../utils/object';
import Header from '../../../../components/ui/Table/Header';
import { getTextWidth } from '../../../../utils/numbers';

const domainIcons = {
  'gmail.com': Gmail,
  'googleadservices.com': GoogleAds,
  linkedin: LinkedIn,
  Search,
  twitter: Twitter,
  'x.com': Twitter,
  Twitter,
  Google: GoogleAds,
  LinkedIn,
  'linkedin.com': LinkedIn,
  'lnkd.in': LinkedIn,
  Youtube,
  'youtube.com': Youtube,
};

const getDomain = (url) => {
  const match = url?.match(/(?:https?:\/\/)?(?:www\.)?([^\\/]+)/i);
  const domain = match ? match[1].split('.').slice(-2).join('.') : url;
  return domain;
};

const getDomainIcon = (domain) => {
  const IconComponent = domainIcons[domain] || null;
  return IconComponent ? (
    <IconComponent
      style={{
        width: '25px',
        height: '25px',
        marginRight: '8px',
        marginBottom: '0.25em',
      }}
    />
  ) : (
    <img
      src={`https://${domain}/favicon.ico`}
      alt={`${domain} icon`}
      style={{
        width: '25px',
        height: '25px',
        marginRight: '8px',
        marginBottom: '0.25em',
      }}
      onError={(e) => {
        e.target.src = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
      }}
    />
  );
};

const List = ({
  data,
  isLoading,
  page,
  setPage,
  orderBy,
  setOrderBy,
  order,
  setOrder,
  limit,
  setLimit,
  title,
  showUniqueVisitorsColumn,
  filterParams,
  attributionParams,
  isTaggedOptionSelected,
}) => {
  const [getWebSDKAttributionListCSV, downloadResponse] =
    webSDKApi.useGetWebSDKAttributionListCSVMutation();
  const [tableData, setTableData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const { id } = useParams();
  const header = [
    {
      field: '',
      title: '#',
      width: '32px',
    },
    {
      field: 'source_field',
      title,
    },
    ...(showUniqueVisitorsColumn
      ? [
          {
            field: 'unique_visitors',
            title: 'Visitors',
          },
        ]
      : []),
    {
      field: 'new_visitors',
      title: 'New Visitors',
    },
    {
      field: 'wallet_holders',
      title: 'With Wallets',
    },
    {
      field: 'web3_adoption',
      title: 'Web3 Adoption',
    },
    {
      field: 'wallets_connected',
      title: 'Wallets Connected',
    },
    {
      field: 'median_buying_power',
      title: 'Median Buying Power',
    },
    {
      field: 'visitors_converted',
      title: 'Converted',
    },
  ];

  useEffect(() => {
    if (downloadResponse.isLoading) {
      setIsOpen(true);
    }
    if (downloadResponse.isSuccess && downloadResponse.data) {
      const handleOpen = () => {
        setTimeout(() => {
          window.location.replace(downloadResponse.data[0].link);
        }, 1000);
      };
      handleOpen();
    }
    if (downloadResponse.error?.status === 403) {
      setIsOpen(false);
    }
    return () => clearTimeout();
  }, [downloadResponse]);

  useEffect(() => {
    if (data?.data.length) {
      const newVisitorsWidth = `${getTextWidth(getMaxValueByProperty(data.data, 'new_visitors'), 14) + 12}px`;
      const walletsConnectedWidth = `${getTextWidth(getMaxValueByProperty(data.data, 'wallets_connected'), 14) + 12}px`;
      const visitorsConvertedWidth = `${getTextWidth(getMaxValueByProperty(data.data, 'visitors_converted'), 14) + 12}px`;

      const temp = data?.data?.map((elem, index) => ({
        pageNumber: (
          <span className={styles.number}>
            {(page - 1) * limit + index + 1}
          </span>
        ),
        source_field: (
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              {getDomainIcon(getDomain(elem.source_field))}
              <span className={`${styles.content_value} text-truncate`}>
                {getDomain(elem.source_field) || 'Organic'}
              </span>
            </div>
          </div>
        ),
        ...(showUniqueVisitorsColumn && {
          unique_visitors: (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-2 align-items-center">
                <span className={`${styles.content_value} text-truncate`}>
                  {ethFormat(elem.unique_visitors, 1)}
                </span>
              </div>
            </div>
          ),
        }),
        new_visitors: (
          <div className={`d-flex align-items-center`}>
            <span style={{ minWidth: newVisitorsWidth }}>
              {ethFormat(elem.new_visitors, 1)}
            </span>
            {elem.unique_visitors ? (
              <span className={styles.gray}>
                {(elem.new_visitors / elem.unique_visitors) * 100 >= 0.01
                  ? ethFormat(
                      (elem.new_visitors / elem.unique_visitors) * 100,
                      1
                    )
                  : '<0.01'}
                %
              </span>
            ) : null}
          </div>
        ),
        wallet_holders: (
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2 align-items-center">
              <span className={`${styles.content_value} text-truncate`}>
                {ethFormat(elem.wallet_holders, 1)}
              </span>
            </div>
          </div>
        ),
        web3_adoption: (
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2 align-items-center">
              <span className={`${styles.content_value} text-truncate`}>
                {elem.web3_adoption}
              </span>
            </div>
          </div>
        ),
        wallets_connected: (
          <div className={`d-flex align-items-baseline`}>
            <span style={{ minWidth: walletsConnectedWidth }}>
              {ethFormat(elem.wallets_connected, 1)}
            </span>
            <span className={styles.gray}>
              {(elem.wallets_connected / elem.wallet_holders) * 100 >= 0.01
                ? ethFormat(
                    (elem.wallets_connected / elem.wallet_holders) * 100,
                    1
                  )
                : '<0.01'}
              %
            </span>
          </div>
        ),
        median_buying_power: (
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2 align-items-center">
              <span className={`${styles.content_value} text-truncate`}>
                {getEthPrice(elem?.median_buying_power, true)}
              </span>
            </div>
          </div>
        ),
        visitors_converted: (
          <div className="d-flex align-items-baseline">
            <span style={{ minWidth: visitorsConvertedWidth }}>
              {ethFormat(elem.visitors_converted, 1)}
            </span>
            <span className={styles.gray}>{elem.conversion_rate}</span>
          </div>
        ),
      }));
      setTableData(temp);
    } else {
      setTableData([]);
    }
  }, [data, page, limit, showUniqueVisitorsColumn]);

  return (
    <>
      <Header
        title={`${title} List`}
        info={counter(data?.count, page, limit)}
        actions={
          <Export
            onClick={() =>
              getWebSDKAttributionListCSV({
                id,
                limit: 50000,
                orderBy,
                order: order ? 'desc' : 'asc',
                ...filterParams,
                ...omitKey(attributionParams, 'conversion_event'),
                ...(isTaggedOptionSelected
                  ? {
                      tagged_conversion_event:
                        attributionParams.conversion_event,
                    }
                  : { conversion_event: attributionParams.conversion_event }),
              })
            }
            tooltip={'Export attribution list to CSV'}
            tooltipWarnings={['No more than 50,000 entries will be downloaded']}
          />
        }
      />
      <div className="w-100">
        <Table
          total={data?.count || 0}
          data={tableData}
          desc={order}
          setDesc={setOrder}
          ordering={orderBy}
          setOrdering={setOrderBy}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
          headers={header}
          isLoading={isLoading}
          min={25}
        />
      </div>
      {isOpen && downloadResponse?.error?.status !== 403 ? (
        <DownloadModal result={downloadResponse} setIsOpen={setIsOpen} />
      ) : null}
    </>
  );
};

export default List;
